'use client';
import { createContext, useContext, useMemo } from 'react';
import type { Locale } from '@/lib/i18n/settings';
import type { TranslationKey, TranslationValues } from '@/lib/i18n/types';
import type { Dictionary } from '@/lib/i18n/translate';
import { getPluralForm } from '@/lib/i18n/plural';

const I18nContext = createContext<{ locale: Locale; dict: Dictionary } | null>(null);

export function I18nProvider({
                                 locale,
                                 dictionary,
                                 children,
                             }: {
    locale: Locale;
    dictionary: Dictionary;
    children: React.ReactNode;
}) {
    const value = useMemo(() => ({ locale, dict: dictionary }), [locale, dictionary]);
    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

function getIn(dict: Dictionary, path: string[]): unknown {
    let current: unknown = dict;

    for (const segment of path) {
        if (current && typeof current === 'object' && segment in current) {
            current = (current as Dictionary)[segment];
        } else {
            return undefined;
        }
    }

    return current;
}

export function useTranslation() {
    const ctx = useContext(I18nContext);
    if (!ctx) throw new Error('useTranslation must be used within I18nProvider');

    const t = (key: TranslationKey, values?: TranslationValues, fallback?: string) => {
        let lookupKey: string = key;

        if (values && typeof values.count === 'number') {
            const form = getPluralForm(ctx.locale, values.count);
            lookupKey = `${key}_${form}`;
        }

        const raw = getIn(ctx.dict, lookupKey.split('.'));
        let result: string = typeof raw === 'string' ? raw : fallback ?? key;

        if (values) {
            result = result.replace(/{{\s*(\w+)\s*}}/g, (_, name) =>
                values[name] !== undefined ? String(values[name]) : `{{${name}}}`
            );
        }

        return result;
    };

    return { t, locale: ctx.locale };
}
